/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jul 02 2025
 **/

import { EventHandler } from "@rfice/eventhandler";
import { useAtomCallback } from "jotai/utils";

import { JotaiStore } from "@/frameworks/libraries";

import { NotificationAtom, NotificationAtomValue, NotificationNoticeAtom } from "./atom";
import UserServiceAPI from "../../API/user/useUsers/UserServiceAPI";
import NoticeServiceAPI from "../../API/user/useNotfice/NoticeServiceAPI";

/**
 * @description
 * GNB 알림 관련 액션 처리 로직
 */
export default function useGNBNotificationActions() {
	// API 호출 로직
	const _readNormal = (readKey: string) => {
		return UserServiceAPI.getInstance().userNotification.readByKey.execute({ readKey });
	};

	const _readAllNormal = async () => {
		return UserServiceAPI.getInstance().userNotification.readAll.execute({ category: "NORMAL" });
	};

	const _readNotice = (noticeId: string) => NoticeServiceAPI.getInstance().notification.read.execute({ noticeId });

	const _readAllNotice = async () => NoticeServiceAPI.getInstance().notification.readAll.execute({});

	/**
	 * @description
	 * 종모양 알림 Normal Type 읽음 처리
	 */
	const handleReadNormal = useAtomCallback(
		async (_, set, readKey: string) => {
			await _readNormal(readKey);
			EventHandler.emit("notificationLogs:readByKey", readKey);
			set(NotificationAtom, (prev: NotificationAtomValue) => {
				return new Map(prev!.set(readKey, { ...prev.get(readKey)!, read: true }));
			});
		},
		{
			store: JotaiStore
		}
	);

	/**
	 * @description
	 * 종모양 알림 Normal Type 전체 읽음 처리
	 */
	const handleReadAllNormal = useAtomCallback(
		async (_, set) => {
			await _readAllNormal();
			EventHandler.emit("notificationLogs:readAll");
			set(NotificationAtom, (prev) => {
				const notiList = Array.from(prev.entries());
				notiList.forEach((val) => {
					//TODO: 런타임내 타입이 안맞는 메시지가 있음
					if (val[1]?.payload?.readKey) {
						EventHandler.emit("notificationLogs:readByKey", val[1].payload.readKey, true);
					}
				});
				return new Map(notiList.map(([key, value]) => [key, { ...value, read: true }]));
			});
		},
		{
			store: JotaiStore
		}
	);

	/**
	 * @description
	 * 종모양 알림 Notice Type 읽음 처리
	 */
	const handleReadNotice = useAtomCallback(
		async (_, set, noticeId: string) => {
			await _readNotice(noticeId);
			set(NotificationNoticeAtom, (prev) => {
				return new Map(prev!.set(noticeId, { ...prev.get(noticeId)!, read: true }));
			});
		},
		{
			store: JotaiStore
		}
	);

	/**
	 * @description
	 * 종모양 알림 Notice Type 전체 읽음 처리
	 */
	const handleReadAllNotice = useAtomCallback(
		async (_, set) => {
			await _readAllNotice();
			set(NotificationNoticeAtom, (prev) => {
				const notiList = Array.from(prev.entries());
				notiList.forEach((val) => {
					if (val[1]?.payload?.readKey) {
						EventHandler.emit("notificationLogs:readByKey", val[1].payload.readKey, true);
					}
				});
				return new Map(notiList.map(([key, value]) => [key, { ...value, read: true }]));
			});
		},
		{
			store: JotaiStore
		}
	);

	return {
		handleReadNormal,
		handleReadAllNormal,
		handleReadNotice,
		handleReadAllNotice
	};
}
